const SECTION_ID = 'sito-product-family-data';
const CARD_SELECTOR = '[data-sito-product-card], product-card';

let catalogPromise;

function parseSectionPayload(responseText) {
  let markup = responseText;

  if (responseText.trim().startsWith('{')) {
    const sections = JSON.parse(responseText);
    markup = sections[SECTION_ID] || '';
  }

  const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
  const dataNode = documentFragment.querySelector('[data-sito-product-family-data]');
  if (!dataNode?.textContent) throw new Error('Sito product family data was not returned.');

  return JSON.parse(dataNode.textContent);
}

async function fetchCatalogPage(page) {
  const root = window.Shopify?.routes?.root || '/';
  const url = new URL(`${root}collections/all`, window.location.origin);
  url.searchParams.set('view', 'sito-family-data');
  url.searchParams.set('section_id', SECTION_ID);
  url.searchParams.set('page', String(page));

  const response = await fetch(url, { headers: { Accept: 'text/html' } });
  if (!response.ok) throw new Error(`Unable to load product families (${response.status}).`);

  return parseSectionPayload(await response.text());
}

async function loadCatalog() {
  const firstPage = await fetchCatalogPage(1);
  const pages = [firstPage];

  for (let page = 2; page <= firstPage.pages; page += 1) {
    pages.push(await fetchCatalogPage(page));
  }

  const products = pages.flatMap((payload) => payload.products || []);
  const families = new Map();

  products.forEach((product) => {
    if (!product.family) return;
    const siblings = families.get(product.family) || [];
    siblings.push(product);
    families.set(product.family, siblings);
  });

  return { products, families };
}

function getCatalog() {
  catalogPromise ||= loadCatalog().catch((error) => {
    console.warn('[Sito product families]', error);
    return { products: [], families: new Map() };
  });

  return catalogPromise;
}

function createThumbnail(product, active, mode) {
  const element = document.createElement(mode === 'pdp' ? 'a' : 'button');
  element.className = 'sito-family-swatches__option';
  element.dataset.productId = String(product.id);
  element.setAttribute('aria-label', `${product.label}: ${product.formattedPrice}`);

  if (mode === 'pdp') {
    element.href = product.url;
    if (active) element.setAttribute('aria-current', 'page');
  } else {
    element.type = 'button';
    element.setAttribute('aria-pressed', String(active));
  }

  if (active) element.classList.add('is-active');

  if (product.primaryImage) {
    const image = document.createElement('img');
    image.src = product.primaryImage;
    image.alt = '';
    image.loading = 'lazy';
    image.width = mode === 'pdp' ? 64 : 56;
    image.height = mode === 'pdp' ? 64 : 56;
    element.append(image);
  }

  return element;
}

function createBadge(label, modifier = '', sito = false) {
  const badge = document.createElement('div');
  badge.className = sito
    ? `sito-product-grids__badge ${modifier}`.trim()
    : `product-badges__badge product-badges__badge--rectangle ${modifier}`.trim();
  badge.textContent = label;
  return badge;
}

function updateBadges(card, product) {
  const leftContainer = card.querySelector('.product-badges--top-left, .sito-product-grids__badges--left');
  const rightContainer = card.querySelector('.product-badges--top-right, .sito-product-grids__badges--right');
  if (!leftContainer || !rightContainer) return;

  leftContainer.replaceChildren();
  rightContainer.replaceChildren();

  const sito = leftContainer.classList.contains('sito-product-grids__badges');
  const badges = (Array.isArray(product.badges) ? product.badges : [])
    .map((label) => ({ label: String(label).trim(), normalized: String(label).trim().toLowerCase() }))
    .filter(({ label }) => label !== '');
  const recognizedBadges = new Set(['new arrival', 'sold out', 'sale', 'blue light', 'polarised']);

  if (badges.some(({ normalized }) => normalized === 'new arrival')) {
    leftContainer.append(createBadge('NEW ARRIVAL', 'sito-badge--new-arrival', sito));
  }

  let rightBadge = null;
  if (!product.available) {
    rightBadge = { label: product.soldOutLabel, modifier: 'sito-badge--sold-out' };
  } else if (
    Number(product.compareAtPrice) > Number(product.price) ||
    badges.some(({ normalized }) => normalized === 'sale')
  ) {
    rightBadge = { label: product.saleLabel, modifier: 'sito-badge--sale' };
  } else if (badges.some(({ normalized }) => normalized === 'blue light')) {
    rightBadge = { label: 'BLUE LIGHT', modifier: 'sito-badge--blue-light' };
  } else if (badges.some(({ normalized }) => normalized === 'polarised')) {
    rightBadge = { label: 'POLARISED', modifier: 'sito-badge--polarised' };
  } else {
    const fallbackBadge = badges.find(({ normalized }) => !recognizedBadges.has(normalized));
    if (fallbackBadge) {
      rightBadge = { label: fallbackBadge.label, modifier: 'sito-badge--fallback' };
    }
  }

  if (rightBadge) {
    rightContainer.append(createBadge(rightBadge.label, rightBadge.modifier, sito));
  }
}

function updateLinks(card, product) {
  card.querySelectorAll('a[href]').forEach((link) => {
    if (
      link.matches('[ref="productCardLink"], [ref="cardGalleryLink"], [ref="productTitleLink"], [data-sito-product-link]')
    ) {
      link.href = product.url;
    }
  });
}

function updateTitle(card, product) {
  const nativeTitleLink = card.querySelector('[ref="productTitleLink"]');
  const nativeTitle = nativeTitleLink?.querySelector('p, .text-block');
  const sitoTitleLink = card.querySelector('.sito-product-grids__product-title [data-sito-product-link]');

  if (nativeTitle) nativeTitle.textContent = product.title;
  if (nativeTitleLink) nativeTitleLink.setAttribute('aria-label', product.title);
  if (sitoTitleLink) sitoTitleLink.textContent = product.title;

  const hiddenTitle = card.querySelector('[ref="productCardLink"] .visually-hidden');
  if (hiddenTitle) hiddenTitle.textContent = product.title;
}

function updatePrice(card, product) {
  const sitoPrice = card.querySelector('.sito-product-grids__product-price');
  if (sitoPrice) sitoPrice.textContent = product.formattedPrice;

  const nativePrice = card.querySelector('product-price [ref="priceContainer"]');
  if (!nativePrice) return;

  const price = document.createElement('span');
  price.className = 'price';
  price.textContent = product.formattedPrice;
  nativePrice.replaceChildren(price);

  if (product.compareAtPrice > product.price) {
    const compareAt = document.createElement('span');
    compareAt.className = 'compare-at-price';
    compareAt.textContent = product.formattedCompareAtPrice;
    nativePrice.append(compareAt);
  }

  nativePrice.closest('product-price')?.setAttribute('data-product-id', String(product.id));
}

function updateSitoMedia(card, product) {
  const primary = card.querySelector('.sito-product-grids__img--primary');
  let secondary = card.querySelector('.sito-product-grids__img--secondary');

  if (primary && product.primaryImage) {
    primary.removeAttribute('srcset');
    primary.src = product.primaryImage;
    primary.alt = product.primaryAlt || product.title;
  }

  if (!secondary && product.secondaryImage && primary) {
    secondary = document.createElement('img');
    secondary.className = 'sito-product-grids__img sito-product-grids__img--secondary';
    primary.after(secondary);
  }

  if (secondary && product.secondaryImage) {
    secondary.removeAttribute('srcset');
    secondary.src = product.secondaryImage;
    secondary.alt = product.secondaryAlt || product.title;
    secondary.hidden = false;
  } else if (secondary) {
    secondary.hidden = true;
  }
}

function updateNativeMedia(card, product) {
  const gallery = card.querySelector('.card-gallery');
  if (!gallery || !product.primaryImage) return;

  let layer = gallery.querySelector('.sito-family-card-media');
  if (!layer) {
    layer = document.createElement('div');
    layer.className = 'sito-family-card-media';
    layer.innerHTML = '<img class="sito-family-card-media__image sito-family-card-media__image--primary" alt=""><img class="sito-family-card-media__image sito-family-card-media__image--secondary" alt="">';
    gallery.append(layer);
  }

  const primary = layer.querySelector('.sito-family-card-media__image--primary');
  const secondary = layer.querySelector('.sito-family-card-media__image--secondary');
  primary.src = product.primaryImage;
  primary.alt = product.primaryAlt || product.title;

  if (product.secondaryImage) {
    secondary.src = product.secondaryImage;
    secondary.alt = product.secondaryAlt || product.title;
    layer.classList.add('has-secondary');
  } else {
    secondary.removeAttribute('src');
    secondary.alt = '';
    layer.classList.remove('has-secondary');
  }

  gallery.dataset.productId = String(product.id);
  card.dataset.featuredMediaUrl = product.primaryImage;
}

function updateQuickAdd(card, product) {
  const quickAdd = card.querySelector('quick-add-component');
  if (!quickAdd) return;

  quickAdd.dataset.productTitle = product.title;
  quickAdd.dataset.productOptionsCount = String(product.optionsCount);
  quickAdd.dataset.quickAddButton = product.quickAddMode;

  const formComponent = quickAdd.querySelector('product-form-component');
  if (formComponent) formComponent.dataset.productId = String(product.id);

  const variantInput = quickAdd.querySelector('input[name="id"]');
  if (variantInput) {
    variantInput.value = String(product.variantId);
    variantInput.disabled = !product.available;
  }

  const addButton = quickAdd.querySelector('.quick-add__button--add');
  const chooseButton = quickAdd.querySelector('.quick-add__button--choose');
  if (addButton) addButton.disabled = !product.available;
  if (chooseButton) chooseButton.disabled = !product.available;

  const addToCart = quickAdd.querySelector('add-to-cart-component');
  if (addToCart && product.primaryImage) {
    addToCart.dataset.productVariantMedia = product.primaryImage;
  }
}

function updateAnalytics(card, product) {
  const payload = card.getAttribute('view-event-payload');
  if (!payload) return;

  try {
    const data = JSON.parse(payload);
    const productData = data.product || data;

    if ('id' in productData) productData.id = String(product.id);
    if ('title' in productData) productData.title = product.title;
    if ('url' in productData) productData.url = product.url;
    if ('price' in productData) productData.price = product.price;

    const variantData = productData.productVariant || productData.variant;
    if (variantData && typeof variantData === 'object') {
      if ('id' in variantData) variantData.id = String(product.variantId);
      if ('price' in variantData) variantData.price = product.price;
    }

    card.setAttribute('view-event-payload', JSON.stringify(data));
  } catch (error) {
    console.warn('[Sito product families] Unable to update analytics payload.', error);
  }
}

function updateCard(card, product) {
  card.dataset.productId = String(product.id);
  updateLinks(card, product);
  updateTitle(card, product);
  updatePrice(card, product);
  updateBadges(card, product);
  updateQuickAdd(card, product);
  updateAnalytics(card, product);

  if (card.matches('[data-sito-product-card]')) {
    updateSitoMedia(card, product);
  } else {
    updateNativeMedia(card, product);
  }
}

class SitoProductFamilySwatches extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
  }

  async connectedCallback() {
    if (this.dataset.clickHandlerAttached !== 'true') {
      this.dataset.clickHandlerAttached = 'true';
      this.addEventListener('click', this.handleClick);
    }

    if (this.dataset.mode === 'card') {
      const card = this.closest(CARD_SELECTOR);
      const details = card?.querySelector('.product-grid__card > .group-block .group-block-content');
      if (details && this.parentElement !== details) {
        details.append(this);
        return;
      }
    }

    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    const { families } = await getCatalog();
    const siblings = families.get(this.dataset.family) || [];
    if (siblings.length < 2 || !this.isConnected) return;

    this.products = siblings;
    this.render();
  }

  render() {
    const mode = this.dataset.mode || 'card';
    const currentId = String(this.dataset.currentProductId || '');
    this.replaceChildren();

    if (mode === 'pdp') {
      const groups = [
        ['standard', 'Standard Lens'],
        ['polarised', 'Polarised Lens'],
      ];

      groups.forEach(([key, label]) => {
        const products = this.products.filter((product) => product.lensGroup === key);
        if (!products.length) return;

        const group = document.createElement('div');
        group.className = 'sito-family-swatches__group';
        const heading = document.createElement('p');
        heading.className = 'sito-family-swatches__heading';
        heading.textContent = label;
        const options = document.createElement('div');
        options.className = 'sito-family-swatches__options';
        products.forEach((product) => options.append(createThumbnail(product, String(product.id) === currentId, mode)));
        group.append(heading, options);
        this.append(group);
      });
    } else {
      this.classList.add('sito-family-swatches--card');
      this.products.forEach((product) => {
        const option = createThumbnail(product, String(product.id) === currentId, mode);
        this.append(option);
      });
    }

    this.hidden = this.childElementCount === 0;
  }

  selectCardProduct(product) {
    const card = this.closest(CARD_SELECTOR);
    if (!card || String(product.id) === String(this.dataset.currentProductId)) return;

    this.dataset.currentProductId = String(product.id);
    updateCard(card, product);
    this.render();
  }

  handleClick(event) {
    if (this.dataset.mode !== 'card') return;

    const option = event.target.closest('.sito-family-swatches__option');
    if (!option || !this.contains(option)) return;

    event.preventDefault();
    event.stopPropagation();

    const product = this.products?.find(({ id }) => String(id) === option.dataset.productId);
    if (product) this.selectCardProduct(product);
  }
}

if (!customElements.get('sito-product-family-swatches')) {
  customElements.define('sito-product-family-swatches', SitoProductFamilySwatches);
}
